import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { filter, firstValueFrom, timeout } from 'rxjs';

import { AddTaskComponent } from '../add-task/add-task';
import { ProjectService } from '../../../shared/services/project.service';
import { UserService } from '../../../shared/services/user.service';
import { Project, ProjectStage } from '../../../shared/models/project.model';
import { User as UserModel } from '../../../shared/models/user.model';

/** Bounded wait on the shared stores — see TaskDetailPageComponent. */
const STORE_WAIT_MS = 8000;

/**
 * Full-page host for task creation, replacing the modal that used to wrap
 * AddTaskComponent. This component owns no form logic: it only resolves the
 * project/phase context from the route and hands it to AddTaskComponent, which
 * was already written as a page-shaped card rather than a modal.
 */
@Component({
  selector: 'app-task-create-page',
  imports: [CommonModule, AddTaskComponent],
  templateUrl: './task-create-page.html',
})
export class TaskCreatePageComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private projectService = inject(ProjectService);
  private userService = inject(UserService);

  projectId!: string;
  stageId!: string;

  project?: Project;
  stage?: ProjectStage;
  employees: UserModel[] = [];

  loading = true;
  /** Set when the route points at a project or phase that does not exist. */
  notFound = false;

  async ngOnInit() {
    this.projectId = this.route.snapshot.paramMap.get('id')!;
    this.stageId = this.route.snapshot.paramMap.get('stageId')!;

    // No projectService.init() here: app.ts already initialises the stores on sign-in,
    // and init() re-subscribes observeQuery every call. Just wait on the streams,
    // the same way DetailProjectComponent does.
    await this.resolveContext();
  }

  /**
   * Waits for the first non-empty emission before deciding the project is
   * missing — the stores are populated asynchronously, so an early read would
   * report "not found" for a project that is merely still loading.
   */
  private async resolveContext() {
    try {
      const projects = await firstValueFrom(
        this.projectService.projects$.pipe(filter(p => p.length > 0), timeout(STORE_WAIT_MS))
      );
      this.project = projects.find(p => p.projectId === this.projectId);

      const stages = await firstValueFrom(
        this.projectService.projectsStage$.pipe(filter(s => s.length > 0), timeout(STORE_WAIT_MS))
      );
      this.stage = stages.find(s => s.projectStageId === this.stageId);

      if (!this.project || !this.stage) {
        this.notFound = true;
        return;
      }

      if (this.project.tenantId) {
        // Archived users are kept: the assignee picker filters them out itself
        // while still showing anyone already assigned.
        this.employees = await this.userService.getUsersByTenantId(this.project.tenantId);
      }
    } catch (e) {
      console.error('[TaskCreatePage] Failed to resolve project context', e);
      this.notFound = true;
    } finally {
      this.loading = false;
    }
  }

  /** Both a successful create and a cancel land back on the project. */
  goToProject() {
    this.router.navigate(['/projects', this.projectId]);
  }
}
